import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import axiosInstance from '../../api/axiosInstance';

const AdminPmOverview = () => {
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [suspiciousActivity, setSuspiciousActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Filter-States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('lastMessageAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const navigate = useNavigate();

  // Daten laden
  useEffect(() => {
    loadConversations();
    loadStats();
    loadSuspiciousActivity();
  }, [currentPage, searchTerm, selectedUser, dateFrom, dateTo, sortBy, sortOrder]);

  const loadConversations = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        search: searchTerm,
        userId: selectedUser,
        dateFrom,
        dateTo,
        sortBy,
        sortOrder
      });

      const response = await axiosInstance.get(`/admin/pm/conversations?${params}`);
      setConversations(response.data.conversations);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fehler beim Laden der Konversationen:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axiosInstance.get('/admin/pm/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  const loadSuspiciousActivity = async () => {
    try {
      const response = await axiosInstance.get('/admin/pm/suspicious');
      setSuspiciousActivity(response.data.suspicious);
    } catch (error) {
      console.error('Fehler beim Laden verdächtiger Aktivitäten:', error);
    }
  };

  const handleConversationAction = async (conversationId, action, reason = '') => {
    try {
      await axiosInstance.post(`/admin/pm/conversation/${conversationId}/action`, {
        action,
        reason
      });
      
      loadConversations(); // Neu laden
    } catch (error) {
      console.error('Fehler bei Konversations-Aktion:', error);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedUser('');
    setDateFrom('');
    setDateTo('');
    setSortBy('lastMessageAt');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">📬 PM-Verwaltung</h1>
        <div className="text-sm text-gray-500">
          Admin-Übersicht über alle privaten Nachrichten
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs tabs-boxed mb-6">
        <button 
          className={`tab ${activeTab === 'overview' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Übersicht
        </button>
        <button 
          className={`tab ${activeTab === 'conversations' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('conversations')}
        >
          💬 Konversationen
        </button>
        <button 
          className={`tab ${activeTab === 'suspicious' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('suspicious')}
        >
          🚨 Verdächtig
        </button>
      </div>

      {/* Statistiken Tab */}
      {activeTab === 'overview' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="stat bg-base-100 rounded-lg shadow">
            <div className="stat-figure text-primary">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
              </svg>
            </div>
            <div className="stat-title">Gesamt Konversationen</div>
            <div className="stat-value text-primary">{stats.totalConversations.toLocaleString()}</div>
          </div>

          <div className="stat bg-base-100 rounded-lg shadow">
            <div className="stat-figure text-secondary">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="stat-title">Aktive (30 Tage)</div>
            <div className="stat-value text-secondary">{stats.activeConversations.toLocaleString()}</div>
          </div>

          <div className="stat bg-base-100 rounded-lg shadow">
            <div className="stat-figure text-accent">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6z" />
              </svg>
            </div>
            <div className="stat-title">Gesamt Nachrichten</div>
            <div className="stat-value text-accent">{stats.totalMessages.toLocaleString()}</div>
          </div>

          <div className="stat bg-base-100 rounded-lg shadow">
            <div className="stat-figure text-warning">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="stat-title">Gelöschte Nachrichten</div>
            <div className="stat-value text-warning">{stats.deletedMessages.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Konversationen Tab */}
      {activeTab === 'conversations' && (
        <div className="space-y-6">
          {/* Filter */}
          <div className="bg-base-100 p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <input
                type="text"
                placeholder="Suche nach Benutzername oder E-Mail..."
                className="input input-bordered"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <input
                type="date"
                className="input input-bordered"
                placeholder="Von Datum"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <input
                type="date"
                className="input input-bordered"
                placeholder="Bis Datum"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
              <select 
                className="select select-bordered"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="lastMessageAt">Letzte Nachricht</option>
                <option value="createdAt">Erstellt am</option>
                <option value="participantCount">Teilnehmeranzahl</option>
              </select>
              <button 
                className="btn btn-ghost"
                onClick={resetFilters}
              >
                🔄 Reset
              </button>
            </div>
          </div>

          {/* Konversationsliste */}
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Teilnehmer</th>
                  <th>Letzte Nachricht</th>
                  <th>Nachrichten</th>
                  <th>Erstellt</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((conv) => (
                  <tr key={conv._id} className="hover">
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {conv.participantNames.map((participant, idx) => (
                          <div key={idx} className="badge badge-outline">
                            {participant.username}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {conv.lastMessagePreview || 'Keine Nachrichten'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {(conv.lastMessage && conv.lastMessage[0] && conv.lastMessage[0].createdAt) ? 
                          formatDistanceToNow(new Date(conv.lastMessage[0].createdAt), { 
                            addSuffix: true, 
                            locale: de 
                          }) : conv.lastMessageAt ? 
                          formatDistanceToNow(new Date(conv.lastMessageAt), { 
                            addSuffix: true, 
                            locale: de 
                          }) : 'Unbekannt'
                        }
                      </div>
                    </td>
                    <td>
                      <div className="badge badge-info">
                        {conv.messageCount}
                      </div>
                    </td>
                    <td className="text-sm text-gray-500">
                      {conv.createdAt ? 
                        formatDistanceToNow(new Date(conv.createdAt), { 
                          addSuffix: true, 
                          locale: de 
                        }) : 'Unbekannt'
                      }
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {conv.isHidden && (
                          <div className="badge badge-warning">Versteckt</div>
                        )}
                        {conv.isFlagged && (
                          <div className="badge badge-error">Gemeldet</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="dropdown dropdown-end">
                        <label tabIndex={0} className="btn btn-ghost btn-sm">
                          ⚙️
                        </label>
                        <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
                          <li>
                            <button onClick={() => navigate(`/admin/pm/${conv._id}`)}>
                              👁️ Ansehen
                            </button>
                          </li>
                          <li>
                            <button 
                              onClick={() => handleConversationAction(conv._id, conv.isHidden ? 'unhide' : 'hide')}
                              className={conv.isHidden ? 'text-success' : 'text-warning'}
                            >
                              {conv.isHidden ? '👁️ Einblenden' : '🙈 Verstecken'}
                            </button>
                          </li>
                          <li>
                            <button 
                              onClick={() => handleConversationAction(conv._id, conv.isFlagged ? 'unflag' : 'flag')}
                              className={conv.isFlagged ? 'text-success' : 'text-error'}
                            >
                              {conv.isFlagged ? '🚩 Entflaggen' : '🚩 Flaggen'}
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex justify-center">
              <div className="btn-group">
                <button 
                  className="btn"
                  disabled={!pagination.hasPrev}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  «
                </button>
                <button className="btn btn-active">
                  {currentPage} / {pagination.totalPages}
                </button>
                <button 
                  className="btn"
                  disabled={!pagination.hasNext}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Verdächtige Aktivitäten Tab */}
      {activeTab === 'suspicious' && suspiciousActivity && (
        <div className="space-y-6">
          {/* High Volume Users */}
          <div className="bg-base-100 p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              📈 Benutzer mit hohem Nachrichtenvolumen (24h)
            </h3>
            <div className="overflow-x-auto">
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Benutzername</th>
                    <th>E-Mail</th>
                    <th>Nachrichten (24h)</th>
                    <th>Konversationen</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {suspiciousActivity.highVolumeUsers.map((user) => (
                    <tr key={user._id}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>
                        <div className="badge badge-warning">
                          {user.messageCount}
                        </div>
                      </td>
                      <td>{user.uniqueConversations}</td>
                      <td>
                        <button 
                          className="btn btn-xs btn-outline"
                          onClick={() => setSelectedUser(user._id)}
                        >
                          🔍 Filter
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mass Conversations */}
          <div className="bg-base-100 p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              👥 Konversationen mit vielen Teilnehmern
            </h3>
            <div className="overflow-x-auto">
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Teilnehmer</th>
                    <th>Erstellt</th>
                    <th>Letzte Aktivität</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {suspiciousActivity.massConversations.map((conv) => (
                    <tr key={conv._id}>
                      <td>
                        <div className="badge badge-info">
                          {conv.participantCount} Benutzer
                        </div>
                      </td>
                      <td className="text-sm">
                        {conv.createdAt ? 
                          formatDistanceToNow(new Date(conv.createdAt), { 
                            addSuffix: true, 
                            locale: de 
                          }) : 'Unbekannt'
                        }
                      </td>
                      <td className="text-sm">
                        {(conv.lastMessage && conv.lastMessage[0] && conv.lastMessage[0].createdAt) ? 
                          formatDistanceToNow(new Date(conv.lastMessage[0].createdAt), { 
                            addSuffix: true, 
                            locale: de 
                          }) : conv.lastMessageAt ? 
                          formatDistanceToNow(new Date(conv.lastMessageAt), { 
                            addSuffix: true, 
                            locale: de 
                          }) : 'Unbekannt'
                        }
                      </td>
                      <td>
                        <button 
                          className="btn btn-xs btn-outline"
                          onClick={() => navigate(`/admin/pm/${conv._id}`)}
                        >
                          👁️ Ansehen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPmOverview;
